// src/pages/TextTools/SummarizeDocument.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { summarizeDocument, getSummaryStatus } from '../../services/chatApi';
import { FileUp, Copy, Check, XCircle, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

// You can reuse these components from PromptOptimizer.jsx or define them here



const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €"; 

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};

const PurchaseCreditsPrompt = () => {
    const { t } = useTranslation();
    const { user } = useAuth();

    return (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <h2 className="text-2xl font-semibold mb-2 text-slate-800">{t('purchaseCreditsPrompt.title')}</h2>
            <p className="text-slate-800 mb-1">{t('purchaseCreditsPrompt.yourCredits')} <span className="text-brand-orange font-bold">{user?.credits || 0}</span></p>
            <p className="text-slate-800 mb-6 max-w-md">{t('purchaseCreditsPrompt.description')}</p>
            <Link 
                to="/purchase-credits"
                className="px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
            >
                {t('purchaseCreditsPrompt.purchaseButton')}
            </Link>
        </div>
    );
};


const SummarizeDocument = () => {
    const { t, i18n } = useTranslation();
    const { user, setCredits } = useAuth();

    // Simplified state
    const [file, setFile] = useState(null);
    const [fileName, setFileName] = useState('');
    const [summary, setSummary] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasCopied, setHasCopied] = useState(false);

    const pollingIntervalRef = useRef(null);

    useEffect(() => {
        // The returned function is the "cleanup" function.
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, []); 

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            const maxSizeInBytes = 50 * 1024 * 1024;
            if (selectedFile.size > maxSizeInBytes) {
                setError(t('summarizeDocument.errorFileSize', { maxSize: '50 MB' })); // Ensure you have this translation key
                setFile(null); // Clear the file state
                setFileName(''); // Clear the file name display
                // Reset the file input visually
                document.getElementById('file-upload').value = '';
                return;
            }
            const allowedTypes = ['application/pdf', 'text/plain', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
            if (!allowedTypes.includes(selectedFile.type)) {
                setError(t('summarizeDocument.errorFileType'));
                return;
            }
            setFile(selectedFile);
            setFileName(selectedFile.name);
            setError(null);
            setSummary(''); // Clear previous summary
        }
    };
    
    const pollForSummary = (jobId) => {
        pollingIntervalRef.current = setInterval(async () => {
            try {
                const statusResult = await getSummaryStatus(jobId);
                
                // If the poll was successful and we got a result back
                if (statusResult) {
                    if (statusResult.status === 'SUCCESS') {
                        clearInterval(pollingIntervalRef.current);
                        setSummary(statusResult.summary_text);
                        if (statusResult.new_credits !== undefined) {
                            setCredits(statusResult.new_credits);
                        }
                        setIsLoading(false);
                        

                    } else if (statusResult.status === 'FAILURE') {
                        clearInterval(pollingIntervalRef.current);
                        setError(statusResult.error_message || t('summarizeDocument.errorGeneric'));
                        setIsLoading(false);
                    }
                    // If status is 'PENDING', we do nothing and let the interval run again.
                }
            } catch (err) {
                // This catches critical errors with the polling itself (e.g., network down)
                clearInterval(pollingIntervalRef.current);
                setError(err.message);
                setIsLoading(false);
            }
        }, 4000); // Poll every 4 seconds. Adjust as needed.
    };

    const handleSummarize = async () => {
        if (!file) {
            setError(t('summarizeDocument.errorNoFile'));
            return;
        }
        setIsLoading(true);
        setError(null);
        setSummary('');

        try {
            const currentLanguage = i18n.language.split('-')[0];
            // SINGLE API call!
            const initialResponse = await summarizeDocument(file, currentLanguage);

            // Step 2: If we got a job_id, start polling for the result.
            if (initialResponse.job_id) {
                pollForSummary(initialResponse.job_id);
            } else {
                // This would be an unexpected error if the backend doesn't return a job_id
                throw new Error("Failed to start the summarization job.");
            }
        } catch (err) {
            // This catch block now only handles errors from the INITIAL request.
            setError(err.message || t('summarizeDocument.errorGeneric'));
            setIsLoading(false); // Stop loading if the initial submission fails.
        }
    };
    
    const handleClear = () => {
        setFile(null);
        setFileName('');
        setSummary('');
        setError(null);
        // Reset the file input visually
        document.getElementById('file-upload').value = '';
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
        }
    };
    const stripMarkdown = (text) => {
        if (!text) return '';
            return text
              // Remove code blocks first (including language specifier)
              .replace(/```[\s\S]*?```/g, '')
              // Remove inline code
              .replace(/`([^`]+)`/g, '$1')
              // Remove images
              .replace(/!\[([^\]]*)\]\([^\)]+\)/g, '$1')
              // Remove links but keep text
              .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1')
              // Remove bold
              .replace(/(\*\*|__)(.*?)\1/g, '$2')
              // Remove italic
              .replace(/(\*|_)(.*?)\1/g, '$2')
              // Remove strikethrough
              .replace(/~~(.*?)~~/g, '$1')
              // Remove headers
              .replace(/^#{1,6}\s+/gm, '')
              // Remove blockquotes
              .replace(/^\s{0,3}>\s?/gm, '')
              // Remove horizontal rules
              .replace(/^(-{3,}|\*{3,}|_{3,})$/gm, '')
              // Remove list markers
              .replace(/^\s*[-*+]\s+/gm, '')
              .replace(/^\s*\d+\.\s+/gm, '')
              // Remove HTML tags
              .replace(/<[^>]*>/g, '')
              // Clean up multiple newlines
              .replace(/\n{3,}/g, '\n\n')
              // Trim whitespace
              .trim();
      };
    const handleCopy = () => {
        if (summary) {
            navigator.clipboard.writeText(stripMarkdown(summary));
            setHasCopied(true);
            setTimeout(() => setHasCopied(false), 2000);
        }
    };
    
   

    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
        {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2)) ? (
                <SubscribePrompt />
            ) : user?.credits <= 0 ? (
                <PurchaseCreditsPrompt />
            ) : (
            <div className="max-w-4xl mx-auto">
                <h3 className="text-3xl font-bold text-brand-orange">{t('summarizeDocument.title')}</h3>
                <p className="mt-2 text-brand-dark">{t('summarizeDocument.description')}</p>

                {/* File Upload Section */}
                <div className="mt-8">
                     <label htmlFor="file-upload" className="block text-sm font-medium mb-2 text-brand-dark">
                        {t('summarizeDocument.uploadLabel')}
                    </label>
                    <div className="flex items-center p-4 border-2 border-dashed border-slate-400 rounded-md bg-slate-50">
                        <FileUp className="h-8 w-8 text-slate-500" />
                        <label htmlFor="file-upload" className="ml-4 text-sm text-brand-orange font-semibold cursor-pointer">
                            {t('summarizeDocument.chooseFile')}
                            <input id="file-upload" name="file-upload" type="file" className="sr-only" onChange={handleFileChange} accept=".pdf,.txt,.docx" />
                        </label>
                        {fileName && (
                           <div className="ml-auto flex items-center gap-2 text-sm text-slate-700">
                                <span className="truncate max-w-[200px] md:max-w-xs">{fileName}</span>
                                <button onClick={handleClear} title={t('summarizeDocument.clearFile')}>
                                    <XCircle className="h-5 w-5 text-red-500 hover:text-red-700" />
                                </button>
                           </div>
                        )}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{t('summarizeDocument.fileTypes')}</p>
                </div>

                {error && <p className="text-red-500 mt-4">{error}</p>}
                
                <div className="flex items-center space-x-4 mt-6">
                    <button
                        onClick={handleSummarize}
                        className="px-6 py-2 border border-brand-orange hover:bg-brand-orange text-brand-dark font-semibold rounded-md transition-colors disabled:bg-slate-500 disabled:cursor-not-allowed"
                        disabled={isLoading || !file}
                    >
                        {isLoading ? t('summarizeDocument.summarizing') : t('summarizeDocument.summarizeButton')}
                    </button>
                </div>

                {/* Loading Spinner Display */}
                {isLoading && (
                    <div className="mt-8 flex flex-col items-center justify-center py-12">
                        <Loader2 className="h-12 w-12 text-brand-orange animate-spin" />
                        <p className="mt-4 text-slate-600">{t('summarizeDocument.processingMessage') || 'Processing your document...'}</p>
                    </div>
                )}
                
                {/* Result Section (no changes needed) */}
                {summary &&(
                    <div className="mt-10">
                        <h4 className="text-2xl font-semibold">{t('summarizeDocument.resultTitle')}</h4>
                        <div className="relative mt-4 bg-brand-white p-4 border border-slate-700 rounded-md">
                            <button
                                onClick={handleCopy}
                                className="absolute top-2 right-2 p-1.5 bg-slate-200 hover:bg-brand-orange rounded-md"
                                title={t('summarizeDocument.copyButton')}
                            >
                                {hasCopied ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
                            </button>
                            <div className="text-sm text-brand-dark leading-relaxed overflow-auto">
                                <ReactMarkdown 
                                    components={{
                                        // Optional: Custom styling for specific elements
                                        h1: ({node, ...props}) => <h1 className="text-xl font-bold mb-2" {...props} />,
                                        h2: ({node, ...props}) => <h2 className="text-lg font-bold mb-2" {...props} />,
                                        ul: ({node, ...props}) => <ul className="list-disc ml-4 mb-2" {...props} />,
                                        li: ({node, ...props}) => <li className="mb-1" {...props} />,
                                        p: ({node, ...props}) => <p className="mb-2" {...props} />,
                                        strong: ({node, ...props}) => <strong className="font-bold" {...props} />
                                    }}
                                >
                                    {summary}
                                </ReactMarkdown>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            )}
        </div>
    );
};

export default SummarizeDocument;
